import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const CreateProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    iccid: '',
    eid: '',
    carrier: 'MPT',
    activation_policy: 'manual',
    compliance_notes: '',
    status: 'pending',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/profiles', formData);
      toast.success('Profile created successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="create-profile-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard')}
        className="mb-6"
        data-testid="back-button"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Dashboard
      </Button>

      <div className="max-w-3xl">
        <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Create eSIM Profile
        </h1>
        <p className="text-sm text-muted-foreground mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          Enter profile details to generate a new eSIM activation link
        </p>

        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Profile Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-profile-form">
              <div className="space-y-2">
                <Label htmlFor="iccid">ICCID *</Label>
                <Input
                  id="iccid"
                  placeholder="19-20 digit number"
                  value={formData.iccid}
                  onChange={(e) => setFormData({ ...formData, iccid: e.target.value })}
                  required
                  className="font-mono"
                  data-testid="iccid-input"
                />
                <p className="text-xs text-muted-foreground">Integrated Circuit Card Identifier (19-20 digits)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="eid">EID *</Label>
                <Input
                  id="eid"
                  placeholder="32 hexadecimal characters"
                  value={formData.eid}
                  onChange={(e) => setFormData({ ...formData, eid: e.target.value.toUpperCase() })}
                  required
                  className="font-mono"
                  data-testid="eid-input"
                />
                <p className="text-xs text-muted-foreground">eUICC Identifier (32 hex characters)</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Carrier *</Label>
                  <Select value={formData.carrier} onValueChange={(value) => setFormData({ ...formData, carrier: value })}>
                    <SelectTrigger data-testid="carrier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MPT">MPT</SelectItem>
                      <SelectItem value="ATOM">ATOM</SelectItem>
                      <SelectItem value="U9">U9</SelectItem>
                      <SelectItem value="MYTEL">MYTEL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Activation Policy *</Label>
                  <Select
                    value={formData.activation_policy}
                    onValueChange={(value) => setFormData({ ...formData, activation_policy: value })}
                  >
                    <SelectTrigger data-testid="policy-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="compliance_notes">Compliance Notes</Label>
                <Textarea
                  id="compliance_notes"
                  placeholder="Enter any compliance or regulatory notes..."
                  value={formData.compliance_notes}
                  onChange={(e) => setFormData({ ...formData, compliance_notes: e.target.value })}
                  rows={4}
                  data-testid="compliance-notes-input"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? 'Creating...' : 'Create Profile'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} data-testid="cancel-button">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateProfile;
